// Colored names (#540, #550, #698): the author name in the Classic and Bubbles rows, the @name pills and the typing line wear the
// person's color. Which color: the #rrggbb the server stores for the person (`Member.nameColor`), the same in every server, for every
// viewer and in every theme. Nothing here decides a color: a person who is not in the member list (left the server, a DM) gets the hash
// color of their id (`nameColorHash`), @Bloom the plain color. The IRC layout keeps its own NICK_COLORS and does not read any of this.
//
// The setting is per device, on by default: Settings → Appearance → Colored names. localStorage `bloom:colored-names`, applied as
// <html data-colored-names="on|off"> so CSS turns every name plain at once, with no JS per row (like layout.ts).
// Storage can be missing or throw (private windows, blocked site data): then the choice lasts until reload.
import { createContext, useContext, useSyncExternalStore, type CSSProperties } from "react";
import { BLOOM_ID, nameColorOf } from "@bloom/shared";

export const COLORED_NAMES_KEY = "bloom:colored-names";

/** A stored value as the setting: only "off" turns it off; anything else (missing, old, edited by hand) is the default, on. */
export function parseColoredNames(raw: unknown): boolean {
  return raw !== "off";
}

function stored(): boolean {
  try {
    return parseColoredNames(localStorage.getItem(COLORED_NAMES_KEY));
  } catch {
    return true;
  }
}

const listeners = new Set<() => void>();
let current = stored();

function apply() {
  if (typeof document !== "undefined") document.documentElement.dataset.coloredNames = current ? "on" : "off";
  for (const fn of listeners) fn();
}

export const getColoredNames = () => current;

export function setColoredNames(next: boolean) {
  current = next;
  try {
    localStorage.setItem(COLORED_NAMES_KEY, next ? "on" : "off");
  } catch {
    // private mode: still applies for this page
  }
  apply();
}

export function installColoredNames() {
  current = stored();
  apply();
  // Another tab changed it.
  addEventListener("storage", (e) => {
    if (e.key !== COLORED_NAMES_KEY) return;
    current = stored();
    apply();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useColoredNames = (): boolean => useSyncExternalStore(subscribe, () => current);

/** The stored color (#rrggbb) of every current member of the open server (`Member.nameColor`), or null in the IRC layout (plain names). */
export const NameColorsContext = createContext<ReadonlyMap<string, string> | null>(new Map());

/** What to put on a name: the class `nc` and the `--nc` variable (CSS draws the color unless the setting is off), or nothing. */
export interface NameLook {
  className?: string;
  style?: CSSProperties;
}

const NONE: NameLook = {};

/** The look of a person's name: their stored color, else (not in the member list) the hash color of their id. @Bloom and no id: plain. */
export function nameLook(colors: ReadonlyMap<string, string> | null, id: string | null | undefined): NameLook {
  if (!colors || !id || id === BLOOM_ID) return NONE;
  return { className: "nc", style: { ["--nc" as string]: nameColorOf(id, colors.get(id)) } };
}

export function useNameLook(id: string | null | undefined): NameLook {
  return nameLook(useContext(NameColorsContext), id);
}
